import React, { useState } from 'react';
import { X, Download, Printer, Trash2 } from 'lucide-react';
import AssetImagePreview from '../../components/AssetImagePreview';

const AssetDetailModal = ({ asset, onClose, buildings, setCurrentImageIndex, setModalImages, setShowImageModal, deleteAsset, loading, hasPermission }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleImageViewInModal = (imageIndex) => {
    setCurrentImageIndex(imageIndex);
    setModalImages(asset.image_metadata || []);
    setShowImageModal(true);
  };

  const generateAssetPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const { jsPDF } = await import("jspdf");
      await import("jspdf-autotable");
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;
      let y = 30;

      doc.setFontSize(20);
      doc.text("Asset Information", pageWidth / 2, y, { align: "center" });
      y += 20;

      if (asset.asset_tag) {
        doc.setFontSize(16);
        doc.text(`Asset Tag: ${asset.asset_tag}`, pageWidth / 2, y, { align: "center" });
        y += 15;
      }

      if (asset.qr_code) {
        try {
          const qrSize = 60;
          doc.addImage(asset.qr_code, "PNG", (pageWidth - qrSize) / 2, y, qrSize, qrSize);
          y += qrSize + 10;
          doc.setFontSize(10);
          doc.text("Scan QR code to access this asset", pageWidth / 2, y, { align: "center" });
          y += 20;
        } catch { y += 10; }
      }

      doc.setFontSize(14);
      doc.text("Asset Details", margin, y);
      y += 15;
      doc.setFontSize(11);

      const fields = [
        { label: "Name", value: asset.name },
        { label: "Description", value: asset.description },
        { label: "Location", value: asset.location },
        { label: "Category", value: asset.category },
        { label: "Manufacturer", value: asset.manufacturer },
        { label: "Model", value: asset.model },
        { label: "Serial Number", value: asset.serial_number },
        { label: "Assigned To", value: asset.assigned_to },
        { label: "Status", value: asset.status },
        {
          label: "Assigned Property",
          value: (() => {
            if (!asset.building_id) return null;
            const b = buildings.find(b => b.id === asset.building_id);
            return b ? `${b.buildingName} (${b.city || "No City"})` : `Property ID: ${asset.building_id}`;
          })(),
        },
        { label: "Created", value: new Date(asset.created_at).toLocaleDateString() },
      ];

      fields.forEach(field => {
        if (!field.value) return;
        doc.setFont(undefined, "bold");
        doc.text(`${field.label}:`, margin, y);
        doc.setFont(undefined, "normal");
        const lines = doc.splitTextToSize(String(field.value), pageWidth - margin * 2 - 60);
        doc.text(lines, margin + 60, y);
        y += 8 * lines.length;
        if (y > 250) { doc.addPage(); y = 30; }
      });

      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(8);
      doc.text(`Generated on ${new Date().toLocaleString()}`, margin, pageHeight - 20);
      doc.text("Fortress Asset Managers", pageWidth - margin, pageHeight - 20, { align: "right" });
      return doc;
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const doc = await generateAssetPDF();
      doc.save(`asset_${(asset.asset_tag || asset.name).replace(/[^a-z0-9]/gi, "_").toLowerCase()}_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch { alert("Failed to generate PDF. Please try again."); }
  };

  const handlePrintPDF = async () => {
    try {
      const doc = await generateAssetPDF();
      doc.autoPrint();
      window.open(doc.output("bloburl"), "_blank");
    } catch { alert("Failed to generate PDF for printing. Please try again."); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-bold text-gray-900">{asset.name}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          <div className="space-y-6">
            {asset.asset_tag && (
              <div className="text-center">
                <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-lg text-sm font-mono font-semibold">
                  {asset.asset_tag}
                </span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Images</label>
              <AssetImagePreview
                images={asset.image_metadata || []}
                onImageView={handleImageViewInModal}
                editable={false}
                className="bg-gray-50 rounded-lg p-4"
              />
            </div>

            {asset.qr_code && (
              <div className="text-center">
                <img src={asset.qr_code} alt="QR Code" className="mx-auto w-32 h-32 border rounded-lg" />
                <p className="text-sm text-gray-500 mt-2">Scan to access this asset</p>
              </div>
            )}

            {asset.description && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <p className="text-gray-600">{asset.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {[
                ["Location", asset.location],
                ["Category", asset.category],
                ["Manufacturer", asset.manufacturer],
                ["Model", asset.model],
                ["Serial Number", asset.serial_number],
                ["Assigned To", asset.assigned_to],
              ].map(([label, value]) => value ? (
                <div key={label}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <p className="text-gray-600 font-mono text-sm">{value}</p>
                </div>
              ) : null)}

              {asset.building_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Property</label>
                  <p className="text-gray-600">
                    {(() => {
                      const b = buildings.find(b => b.id === asset.building_id);
                      return b ? `${b.buildingName} (${b.city || "No City"})` : `Property ID: ${asset.building_id}`;
                    })()}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <p className="text-gray-600 capitalize">{asset.status}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
              <p className="text-gray-600">{new Date(asset.created_at).toLocaleDateString()}</p>
            </div>

            <div className="flex gap-2 pt-4">
              <button onClick={handleDownloadPDF} disabled={isGeneratingPDF}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2">
                {isGeneratingPDF ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Generating...</> : <><Download size={16} />Download PDF</>}
              </button>
              <button onClick={handlePrintPDF} disabled={isGeneratingPDF}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2">
                {isGeneratingPDF ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Generating...</> : <><Printer size={16} />Print PDF</>}
              </button>
            </div>

            {hasPermission("edit_asset_details") && (
              <div className="flex gap-2 pt-2">
                <button onClick={() => deleteAsset(asset.id)} disabled={loading}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 flex items-center justify-center gap-2">
                  <Trash2 size={16} />Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetDetailModal;
